import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Clock, CreditCard, ShieldCheck, Sparkles, Users, Star, Calendar, ChevronDown, ChevronRight, Stethoscope, Activity, Heart, Award, ArrowRight, Phone, MapPin, Mail } from 'lucide-react';
const HERO_BG = 'https://res.cloudinary.com/magic-patterns/image/upload/v1741132711/c909623e-670d-4560-b8d1-96898d249f03.jpg';
const LIFESTYLE_IMG = 'https://cdn.magicpatterns.com/patterns/generated-images/4e5d4e07-0f6d-4a3a-9e0a-d86828dffab5.jpg';
const AVATARS = ['https://cdn.magicpatterns.com/patterns/generated-images/2825aa0a-1228-4c1f-b4da-f2476193c85a.jpg', 'https://cdn.magicpatterns.com/patterns/generated-images/f4404c1d-4b3d-40d8-b39c-fca6983b2a63.jpg', 'https://cdn.magicpatterns.com/patterns/generated-images/0b2f6062-d9b6-4cb2-ac67-8d909917421b.jpg'];
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function App() {
  return <div className="min-h-screen bg-brand-light font-sans text-slate-800 overflow-x-hidden" data-id="element-0">
      <Navbar data-id="element-1" />
      <HeroSection data-id="element-2" />
      <StatsSection data-id="element-3" />
      <BenefitsSection data-id="element-4" />
      <TimelineSection data-id="element-5" />
      <WhyLoveSection data-id="element-6" />
      <PartnersSection data-id="element-7" />
      <TestimonialsSection data-id="element-8" />
      <FAQSection data-id="element-9" />
      <ContactSection data-id="element-10" />
      <FinalCTASection data-id="element-11" />
      <Footer data-id="element-12" />
    </div>;
}
function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      const sections = ['financing', 'reviews', 'contact'];
      let current = '';
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          // Check if the section is currently in the middle third of the viewport
          if (rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 3) {
            current = section;
          }
        }
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Trigger once on mount to set initial state
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <nav className={`fixed top-0 left-0 right-0 z-50 px-6 lg:px-12 flex justify-between items-center transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg text-brand-navy py-3' : 'bg-transparent text-white py-4'}`} data-id="element-13">
      <div className="flex items-center gap-3" data-id="element-14">
        <img src="https://cdn.magicpatterns.com/uploads/17ULAUNguMCwFMBzuxgSYH/pasted-image.png" alt="Colesville Dental Center Logo" className={`h-10 w-auto object-contain ${!isScrolled ? 'invert brightness-0' : ''}`} data-id="element-15" />
        <span className="text-xl font-bold tracking-tight" data-id="element-16">
          Colesville
        </span>
      </div>
      <div className="hidden md:flex items-center gap-8 text-sm font-medium" data-id="element-17">
        <a href="#financing" className={`transition-colors ${activeSection === 'financing' ? 'text-brand-teal font-bold' : isScrolled ? 'text-slate-600 hover:text-brand-teal' : 'hover:text-brand-teal'}`} data-id="element-18">
          Financing
        </a>
        <a href="#reviews" className={`transition-colors ${activeSection === 'reviews' ? 'text-brand-teal font-bold' : isScrolled ? 'text-slate-600 hover:text-brand-teal' : 'hover:text-brand-teal'}`} data-id="element-19">
          Reviews
        </a>
        <a href="https://colesvilledentalcenter.com/" className={`${isScrolled ? 'bg-brand-teal text-white hover:bg-brand-navy border border-transparent' : 'bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white hover:text-brand-navy'} px-6 py-2.5 rounded-full transition-all duration-300 inline-block`} data-id="element-20">
          Book Consultation
        </a>
      </div>
    </nav>;
}
function HeroSection() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success'>('idle');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('submitting');
    setTimeout(() => {
      setFormState('success');
    }, 1500);
  };
  return <section className="relative min-h-[75vh] flex items-center pt-24 pb-16 lg:py-24" data-id="element-21">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0" data-id="element-22">
        <img src={HERO_BG} alt="Luxury Dental Office" className="w-full h-full object-cover" data-id="element-23" />
        <div className="absolute inset-0 bg-brand-navy/85 mix-blend-multiply" data-id="element-24" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/90 to-transparent" data-id="element-25" />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10" data-id="element-26">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center" data-id="element-27">
          {/* Left Content */}
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-2xl text-white" data-id="element-28">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-teal/20 border border-brand-teal/30 text-brand-mint text-xs font-medium mb-4 backdrop-blur-sm" data-id="element-29">
              <Sparkles className="w-3.5 h-3.5" data-id="element-30" />
              Premium Dental Financing
            </motion.div>

            <motion.h1 variants={fadeInUp} className="text-4xl lg:text-5xl font-bold leading-[1.1] mb-4 tracking-tight" data-id="element-31">
              Your Smile Shouldn't{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-teal to-brand-mint" data-id="element-32">
                Cost You Everything
              </span>
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-base lg:text-lg text-slate-300 mb-6 leading-relaxed font-light" data-id="element-33">
              Quality dental care shouldn't force you to choose between your
              oral health and your financial security. At Colesville Dental
              Center, we believe everyone deserves exceptional care.
            </motion.p>

            <motion.h3 variants={fadeInUp} className="text-lg font-medium text-white mb-6 border-l-2 border-brand-teal pl-4" data-id="element-34">
              Achieve Your Dream Smile Today. Pay Comfortably Tomorrow.
            </motion.h3>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-3 mb-8" data-id="element-35">
              <a href="https://colesvilledentalcenter.com/" className="bg-gradient-to-r from-brand-teal to-brand-accent text-white px-6 py-3 rounded-full font-semibold text-base hover:shadow-lg hover:shadow-brand-teal/30 transition-all duration-300 transform hover:-translate-y-1 flex items-center justify-center gap-2" data-id="element-36">
                Schedule Consultation <ArrowRight className="w-4 h-4" data-id="element-37" />
              </a>
              <a href="#financing" className="px-6 py-3 rounded-full font-semibold text-base border border-white/30 hover:bg-white/10 backdrop-blur-sm transition-all duration-300 flex items-center justify-center" data-id="element-38">
                Explore Financing
              </a>
            </motion.div>

            <motion.div variants={fadeInUp} className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-slate-300" data-id="element-39">
              <div className="flex items-center gap-1.5" data-id="element-40">
                <Clock className="w-3.5 h-3.5 text-brand-teal" data-id="element-41" /> Approved in
                Minutes
              </div>
              <div className="flex items-center gap-1.5" data-id="element-42">
                <CreditCard className="w-3.5 h-3.5 text-brand-teal" data-id="element-43" /> 0%
                Interest Options
              </div>
              <div className="flex items-center gap-1.5" data-id="element-44">
                <Calendar className="w-3.5 h-3.5 text-brand-teal" data-id="element-45" /> Flexible
                Terms
              </div>
              <div className="flex items-center gap-1.5" data-id="element-46">
                <ShieldCheck className="w-3.5 h-3.5 text-brand-teal" data-id="element-47" /> No
                Hidden Fees
              </div>
            </motion.div>
          </motion.div>

          {/* Right Application Card */}
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="hidden lg:block relative" data-id="element-48">
            <div className="absolute inset-0 bg-gradient-to-br from-brand-teal/20 to-brand-accent/20 rounded-3xl blur-2xl transform rotate-3" data-id="element-49"></div>
            <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 p-6 rounded-3xl shadow-2xl" data-id="element-50">
              <div className="flex justify-between items-start mb-6" data-id="element-51">
                <div data-id="element-52">
                  <h3 className="text-xl font-semibold text-white mb-1" data-id="element-53">
                    Prequalify Now
                  </h3>
                  <p className="text-slate-300 text-xs" data-id="element-54">
                    Does not affect your credit score
                  </p>
                </div>
                <div className="bg-white/20 p-2.5 rounded-2xl" data-id="element-55">
                  <ShieldCheck className="w-5 h-5 text-brand-mint" data-id="element-56" />
                </div>
              </div>

              <form className="space-y-3" onSubmit={handleSubmit} data-id="element-57">
                <div className="grid grid-cols-2 gap-3" data-id="element-58">
                  <div className="space-y-1" data-id="element-59">
                    <label className="text-[10px] font-medium text-slate-300 uppercase tracking-wider" data-id="element-60">
                      First Name
                    </label>
                    <input type="text" required className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand-teal transition-colors" placeholder="John" data-id="element-61" />
                  </div>
                  <div className="space-y-1" data-id="element-62">
                    <label className="text-[10px] font-medium text-slate-300 uppercase tracking-wider" data-id="element-63">
                      Last Name
                    </label>
                    <input type="text" required className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand-teal transition-colors" placeholder="Doe" data-id="element-64" />
                  </div>
                </div>
                <div className="space-y-1" data-id="element-65">
                  <label className="text-[10px] font-medium text-slate-300 uppercase tracking-wider" data-id="element-66">
                    Email Address
                  </label>
                  <input type="email" required className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand-teal transition-colors" placeholder="john@example.com" data-id="element-67" />
                </div>
                <div className="space-y-1" data-id="element-68">
                  <label className="text-[10px] font-medium text-slate-300 uppercase tracking-wider" data-id="element-69">
                    Treatment Plan
                  </label>
                  <div className="relative" data-id="element-70">
                    <select required defaultValue="" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand-teal transition-colors appearance-none" data-id="element-71">
                      <option value="" disabled className="bg-brand-navy text-slate-300" data-id="element-72">
                        Select a treatment...
                      </option>
                      <optgroup label="Cosmetic Dentistry" className="bg-brand-navy text-brand-teal font-semibold" data-id="element-73">
                        <option value="teeth-whitening" className="text-white" data-id="element-74">
                          Teeth Whitening
                        </option>
                        <option value="veneers" className="text-white" data-id="element-75">
                          Veneers
                        </option>
                        <option value="bonding" className="text-white" data-id="element-76">
                          Bonding
                        </option>
                      </optgroup>
                      <optgroup label="Restorative Care" className="bg-brand-navy text-brand-teal font-semibold" data-id="element-77">
                        <option value="crowns" className="text-white" data-id="element-78">
                          Crowns
                        </option>
                        <option value="bridges" className="text-white" data-id="element-79">
                          Bridges
                        </option>
                        <option value="root-canals" className="text-white" data-id="element-80">
                          Root Canals
                        </option>
                      </optgroup>
                      <optgroup label="Implant Dentistry" className="bg-brand-navy text-brand-teal font-semibold" data-id="element-81">
                        <option value="single-implants" className="text-white" data-id="element-82">
                          Single Implants
                        </option>
                        <option value="full-arch" className="text-white" data-id="element-83">
                          Full Arch
                        </option>
                      </optgroup>
                      <optgroup label="Emergency & General" className="bg-brand-navy text-brand-teal font-semibold" data-id="element-84">
                        <option value="fillings" className="text-white" data-id="element-85">
                          Fillings
                        </option>
                        <option value="extractions" className="text-white" data-id="element-86">
                          Extractions
                        </option>
                        <option value="cleanings" className="text-white" data-id="element-87">
                          Cleanings
                        </option>
                      </optgroup>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-88" />
                  </div>
                </div>
                <a href="https://colesvilledentalcenter.com/" className="w-full bg-white text-brand-navy font-semibold py-3 rounded-xl mt-2 hover:bg-brand-mint transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-89">
                  <Sparkles className="w-4 h-4" data-id="element-90" /> Check My Rates
                </a>
              </form>

              <div className="mt-5 flex items-center justify-center gap-4 opacity-60 text-xs font-bold text-white tracking-wider" data-id="element-91">
                <span data-id="element-92">CareCredit</span>
                <span className="w-1 h-1 rounded-full bg-white/50" data-id="element-93"></span>
                <span data-id="element-94">LendingClub</span>
                <span className="w-1 h-1 rounded-full bg-white/50" data-id="element-95"></span>
                <span data-id="element-96">Sunbit</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function StatsSection() {
  return <section className="relative z-20 -mt-10 container mx-auto px-6 lg:px-12" data-id="element-97">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6" data-id="element-98">
        {[{
        icon: Users,
        value: '10k+',
        label: 'Happy Patients'
      }, {
        icon: CheckCircle2,
        value: '98%',
        label: 'Approval Rate'
      }, {
        icon: Star,
        value: '5.0',
        label: 'Patient Reviews'
      }, {
        icon: Award,
        value: '20+',
        label: 'Years Serving MD'
      }].map((stat, i) => <motion.div key={i} initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.1
      }} className="bg-white rounded-2xl p-6 shadow-premium border border-slate-100 flex flex-col items-center text-center group hover:-translate-y-1 transition-transform duration-300" data-id="element-99">
            <div className="bg-brand-light p-3 rounded-full mb-4 group-hover:bg-brand-teal/10 transition-colors" data-id="element-100">
              <stat.icon className="w-6 h-6 text-brand-teal" data-id="element-101" />
            </div>
            <div className="text-2xl lg:text-3xl font-bold text-brand-navy mb-1" data-id="element-102">
              {stat.value}
            </div>
            <div className="text-sm text-slate-500 font-medium" data-id="element-103">
              {stat.label}
            </div>
          </motion.div>)}
      </div>
    </section>;
}
function BenefitsSection() {
  const benefits = [{
    icon: CreditCard,
    title: 'Affordable Monthly Payments',
    desc: 'Break down your treatment cost into manageable monthly chunks that fit your budget.'
  }, {
    icon: Sparkles,
    title: 'Zero Interest Options',
    desc: 'Qualify for 0% APR promotional periods ranging from 6 to 24 months.'
  }, {
    icon: Calendar,
    title: 'Flexible Terms',
    desc: 'Choose repayment terms that work for you, up to 60 months for major procedures.'
  }, {
    icon: Clock,
    title: 'Fast Approval',
    desc: 'Apply online or in-office and get a decision in minutes without affecting your credit.'
  }, {
    icon: ShieldCheck,
    title: 'No Hidden Fees',
    desc: 'Complete transparency. No application fees, no prepayment penalties, ever.'
  }, {
    icon: Activity,
    title: 'Multiple Financing Plans',
    desc: 'We partner with several top-tier providers to ensure you get the best possible rate.'
  }];
  return <section id="financing" className="py-24 bg-white" data-id="element-104">
      <div className="container mx-auto px-6 lg:px-12" data-id="element-105">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-106">
          <h2 className="text-sm font-bold text-brand-teal uppercase tracking-widest mb-3" data-id="element-107">
            Why Finance With Us
          </h2>
          <h3 className="text-3xl lg:text-5xl font-bold text-brand-navy mb-6" data-id="element-108">
            Premium Care, Accessible Pricing
          </h3>
          <p className="text-lg text-slate-600" data-id="element-109">
            We've removed the financial barriers to exceptional dentistry.
            Experience the luxury of choice with our tailored payment solutions.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-110">
          {benefits.map((benefit, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="p-8 rounded-3xl bg-brand-light border border-slate-100 hover:shadow-premium hover:bg-white transition-all duration-300 group" data-id="element-111">
              <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-112">
                <benefit.icon className="w-7 h-7 text-brand-teal" data-id="element-113" />
              </div>
              <h4 className="text-xl font-bold text-brand-navy mb-3" data-id="element-114">
                {benefit.title}
              </h4>
              <p className="text-slate-600 leading-relaxed" data-id="element-115">{benefit.desc}</p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
function TimelineSection() {
  const steps = [{
    title: 'Schedule Consultation',
    desc: 'Book your initial visit online or by phone.'
  }, {
    title: 'Treatment Plan',
    desc: 'Receive a personalized, comprehensive plan.'
  }, {
    title: 'Explore Financing',
    desc: 'Review options with our care coordinator.'
  }, {
    title: 'Begin Treatment',
    desc: 'Start your journey to a healthier smile.'
  }, {
    title: 'Enjoy Your Smile',
    desc: 'Experience the confidence of your new smile.'
  }];
  return <section className="py-24 bg-white" data-id="element-116">
      <div className="container mx-auto px-6 lg:px-12" data-id="element-117">
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-118">
          <h2 className="text-3xl lg:text-5xl font-bold text-brand-navy mb-6" data-id="element-119">
            Your Journey to a New Smile
          </h2>
          <p className="text-lg text-slate-600" data-id="element-120">
            A seamless, stress-free process designed around your comfort and
            convenience.
          </p>
        </div>

        <div className="relative" data-id="element-121">
          {/* Desktop Line */}
          <div className="hidden lg:block absolute top-1/2 left-0 w-full h-0.5 bg-slate-100 -translate-y-1/2 z-0" data-id="element-122"></div>

          <div className="flex flex-col lg:flex-row justify-between gap-12 lg:gap-4 relative z-10" data-id="element-123">
            {steps.map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="flex flex-row lg:flex-col items-center lg:text-center gap-6 lg:gap-4 flex-1" data-id="element-124">
                <div className="w-16 h-16 rounded-full bg-white border-4 border-brand-light shadow-lg flex items-center justify-center text-xl font-bold text-brand-teal relative z-10 shrink-0" data-id="element-125">
                  {i + 1}
                </div>
                <div data-id="element-126">
                  <h4 className="text-lg font-bold text-brand-navy mb-2" data-id="element-127">
                    {step.title}
                  </h4>
                  <p className="text-sm text-slate-500" data-id="element-128">{step.desc}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}
function WhyLoveSection() {
  return <section className="py-24 bg-brand-navy text-white overflow-hidden" data-id="element-129">
      <div className="container mx-auto px-6 lg:px-12" data-id="element-130">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-131">
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="relative" data-id="element-132">
            <div className="absolute inset-0 bg-brand-teal/20 rounded-3xl transform -rotate-3 scale-105" data-id="element-133"></div>
            <img src={LIFESTYLE_IMG} alt="Happy Patient" className="relative rounded-3xl shadow-2xl object-cover aspect-[4/3] w-full" data-id="element-134" />

            {/* Floating Badge */}
            <div className="absolute -bottom-8 -right-8 bg-white text-brand-navy p-6 rounded-2xl shadow-xl max-w-xs hidden md:block" data-id="element-135">
              <div className="flex items-center gap-2 mb-2" data-id="element-136">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-5 h-5 fill-brand-teal text-brand-teal" data-id="element-137" />)}
              </div>
              <p className="font-medium text-sm" data-id="element-138">
                "The financing process was so easy, I was able to get my veneers
                the same month!"
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-139">
            <h2 className="text-sm font-bold text-brand-teal uppercase tracking-widest mb-3" data-id="element-140">
              The Colesville Difference
            </h2>
            <h3 className="text-3xl lg:text-5xl font-bold mb-8" data-id="element-141">
              Why Patients Love Our Financing
            </h3>

            <div className="space-y-8" data-id="element-142">
              {[{
              title: 'No Compromises on Care',
              desc: "Don't settle for temporary fixes. Get the comprehensive treatment you actually need, right now."
            }, {
              title: 'Discreet & Confidential',
              desc: 'Your financial information is processed securely and privately by our trusted partners.'
            }, {
              title: 'Dedicated Care Coordinators',
              desc: 'Our team walks you through every option, ensuring you understand your plan perfectly.'
            }].map((item, i) => <div key={i} className="flex gap-4" data-id="element-143">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center shrink-0" data-id="element-144">
                    <CheckCircle2 className="w-6 h-6 text-brand-mint" data-id="element-145" />
                  </div>
                  <div data-id="element-146">
                    <h4 className="text-xl font-bold mb-2" data-id="element-147">{item.title}</h4>
                    <p className="text-slate-300 leading-relaxed" data-id="element-148">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>

            <a href="#contact" className="mt-10 bg-brand-teal text-white px-8 py-4 rounded-full font-semibold hover:bg-brand-accent transition-colors inline-flex items-center gap-2 w-fit" data-id="element-149">
              Speak with a Coordinator <ArrowRight className="w-5 h-5" data-id="element-150" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>;
}
function PartnersSection() {
  return <section className="py-20 bg-brand-light border-y border-slate-200" data-id="element-151">
      <div className="container mx-auto px-6 lg:px-12 text-center" data-id="element-152">
        <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-8" data-id="element-153">
          Trusted Financing Partners
        </p>
        <div className="flex flex-wrap justify-center items-center gap-12 lg:gap-24 opacity-60" data-id="element-154">
          {/* Placeholder for partner logos - using text for now to represent them */}
          <div className="text-2xl font-bold text-slate-800 hover:text-brand-teal transition-colors cursor-pointer grayscale hover:grayscale-0" data-id="element-155">
            CareCredit
          </div>
          <div className="text-2xl font-bold text-slate-800 hover:text-brand-teal transition-colors cursor-pointer grayscale hover:grayscale-0" data-id="element-156">
            Cherry Financing
          </div>
          <div className="text-2xl font-bold text-slate-800 hover:text-brand-teal transition-colors cursor-pointer grayscale hover:grayscale-0" data-id="element-157">
            Sunbit
          </div>
        </div>
      </div>
    </section>;
}
function TestimonialsSection() {
  const testimonials = [{
    name: 'Marcus T.',
    procedure: 'Dental Implants',
    quote: 'I put off getting implants for years because of the cost. The team at Colesville showed me a 24-month zero interest plan that made it completely manageable. Life changing.',
    avatar: AVATARS[0]
  }, {
    name: 'Jennifer K.',
    procedure: 'Veneers',
    quote: "The luxury experience doesn't stop at the waiting room. Their financial coordinator was so discreet and helpful. I have the smile of my dreams and a payment plan I barely notice.",
    avatar: AVATARS[1]
  }, {
    name: 'David R.',
    procedure: 'Full Mouth Restoration',
    quote: 'I thought a full restoration was out of reach. The transparent pricing and flexible terms allowed me to get my health back on track without emptying my savings.',
    avatar: AVATARS[2]
  }];
  return <section id="reviews" className="py-24 bg-white" data-id="element-158">
      <div className="container mx-auto px-6 lg:px-12" data-id="element-159">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-160">
          <h2 className="text-3xl lg:text-5xl font-bold text-brand-navy mb-6" data-id="element-161">
            Real Patient Stories
          </h2>
          <p className="text-lg text-slate-600" data-id="element-162">
            Hear from patients who transformed their smiles through our flexible
            financing options.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8" data-id="element-163">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-brand-light rounded-3xl p-8 relative" data-id="element-164">
              <div className="text-6xl text-brand-teal/20 absolute top-4 right-6 font-serif" data-id="element-165">
                "
              </div>
              <div className="flex items-center gap-2 mb-6" data-id="element-166">
                {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-4 h-4 fill-brand-teal text-brand-teal" data-id="element-167" />)}
              </div>
              <p className="text-slate-700 leading-relaxed mb-8 relative z-10" data-id="element-168">
                "{t.quote}"
              </p>
              <div className="flex items-center gap-4 mt-auto" data-id="element-169">
                <img src={t.avatar} alt={t.name} className="w-12 h-12 rounded-full object-cover" data-id="element-170" />
                <div data-id="element-171">
                  <div className="font-bold text-brand-navy" data-id="element-172">{t.name}</div>
                  <div className="text-sm text-brand-teal" data-id="element-173">{t.procedure}</div>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
function FAQSection() {
  const faqs = [{
    q: 'What credit score do I need to qualify?',
    a: 'While requirements vary by partner, we work with providers that accommodate a wide range of credit profiles, including those with less-than-perfect credit. Many patients are surprised by what they qualify for.'
  }, {
    q: 'How quickly can I be approved?',
    a: "In most cases, you'll receive a decision within minutes of submitting your application, allowing you to proceed with scheduling your treatment immediately."
  }, {
    q: 'Can I pay off my balance early?',
    a: 'Yes! All of our financing partners allow you to pay off your balance early without any prepayment penalties.'
  }, {
    q: 'Do you offer in-house payment plans?',
    a: 'For certain procedures and established patients, we do offer short-term in-house arrangements. Please speak with our care coordinator for details.'
  }, {
    q: 'What procedures qualify for financing?',
    a: 'Virtually all procedures qualify, from cosmetic treatments like veneers and whitening to restorative work like implants and crowns.'
  }];
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="py-24 bg-brand-light" data-id="element-174">
      <div className="container mx-auto px-6 lg:px-12 max-w-4xl" data-id="element-175">
        <div className="text-center mb-16" data-id="element-176">
          <h2 className="text-3xl lg:text-5xl font-bold text-brand-navy mb-6" data-id="element-177">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="space-y-4" data-id="element-178">
          {faqs.map((faq, i) => <div key={i} className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden" data-id="element-179">
              <button className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none" onClick={() => setOpenIndex(openIndex === i ? null : i)} data-id="element-180">
                <span className="font-bold text-brand-navy text-lg" data-id="element-181">
                  {faq.q}
                </span>
                <ChevronDown className={`w-5 h-5 text-brand-teal transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`} data-id="element-182" />
              </button>
              <AnimatePresence data-id="element-183">
                {openIndex === i && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} className="px-6 pb-5 text-slate-600" data-id="element-184">
                    {faq.a}
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>
      </div>
    </section>;
}
function ContactSection() {
  return <section id="contact" className="py-24 bg-brand-light" data-id="element-185">
      <div className="container mx-auto px-6 lg:px-12" data-id="element-186">
        <div className="max-w-5xl mx-auto bg-white rounded-[2.5rem] shadow-premium overflow-hidden flex flex-col md:flex-row" data-id="element-187">
          <div className="md:w-2/5 bg-brand-navy p-10 lg:p-12 text-white flex flex-col justify-between" data-id="element-188">
            <div data-id="element-189">
              <h3 className="text-3xl font-bold mb-4" data-id="element-190">Get in Touch</h3>
              <p className="text-slate-300 text-sm leading-relaxed mb-10" data-id="element-191">
                Have questions about our treatments or financing options? Send
                us a message and our care coordinator will get back to you
                shortly.
              </p>
              <ul className="space-y-6 text-sm" data-id="element-192">
                <li className="flex items-start gap-4" data-id="element-193">
                  <MapPin className="w-5 h-5 text-brand-teal shrink-0 mt-0.5" data-id="element-194" />
                  <span data-id="element-195">
                    8737 Colesville Road #301
                    <br data-id="element-196" />
                    Silver Spring, MD, 20910
                  </span>
                </li>
                <li className="flex items-center gap-4" data-id="element-197">
                  <Phone className="w-5 h-5 text-brand-teal shrink-0" data-id="element-198" />
                  <span data-id="element-199">301-742-2231</span>
                </li>
                <li className="flex items-center gap-4" data-id="element-200">
                  <Mail className="w-5 h-5 text-brand-teal shrink-0" data-id="element-201" />
                  <span data-id="element-202">dentallcentertc@gmail.com</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="md:w-3/5 p-10 lg:p-12" data-id="element-203">
            <form className="space-y-5" onSubmit={e => {
            e.preventDefault();
            alert('Thank you for your message! We will be in touch soon.');
          }} data-id="element-204">
              <div className="space-y-1.5" data-id="element-205">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-206">
                  Full Name
                </label>
                <input type="text" required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-brand-navy focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal transition-all" placeholder="John Doe" data-id="element-207" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-208">
                <div className="space-y-1.5" data-id="element-209">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-210">
                    Email Address
                  </label>
                  <input type="email" required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-brand-navy focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal transition-all" placeholder="john@example.com" data-id="element-211" />
                </div>
                <div className="space-y-1.5" data-id="element-212">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-213">
                    Phone Number
                  </label>
                  <input type="tel" required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-brand-navy focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal transition-all" placeholder="(555) 123-4567" data-id="element-214" />
                </div>
              </div>
              <div className="space-y-1.5" data-id="element-215">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-216">
                  How can we help?
                </label>
                <textarea rows={4} required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-brand-navy focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal transition-all resize-none" placeholder="Tell us about your dental needs..." data-id="element-217"></textarea>
              </div>
              <button type="submit" className="w-full bg-brand-teal text-white font-bold py-4 rounded-xl mt-4 hover:bg-brand-navy transition-colors flex items-center justify-center gap-2" data-id="element-218">
                Send Message <ArrowRight className="w-5 h-5" data-id="element-219" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>;
}
function FinalCTASection() {
  return <section className="py-24 bg-brand-navy relative overflow-hidden" data-id="element-220">
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5" data-id="element-221"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-teal/20 rounded-full blur-[100px] pointer-events-none" data-id="element-222"></div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10 text-center" data-id="element-223">
        <h2 className="text-4xl lg:text-6xl font-bold text-white mb-8 max-w-4xl mx-auto leading-tight" data-id="element-224">
          Ready to Start Your Smile Transformation?
        </h2>

        <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12" data-id="element-225">
          <a href="https://colesvilledentalcenter.com/" className="bg-gradient-to-r from-brand-teal to-brand-accent text-white px-10 py-5 rounded-full font-bold text-lg hover:shadow-lg hover:shadow-brand-teal/30 transition-all duration-300 transform hover:-translate-y-1 inline-block" data-id="element-226">
            Schedule Your Consultation
          </a>
          <a href="#financing" className="px-10 py-5 rounded-full font-bold text-lg text-white border-2 border-white/30 hover:bg-white/10 transition-all duration-300 inline-block" data-id="element-227">
            See Financing Plans
          </a>
        </div>

        <div className="flex flex-wrap justify-center gap-6 text-slate-300 text-sm font-medium" data-id="element-228">
          <div className="flex items-center gap-2" data-id="element-229">
            <ShieldCheck className="w-5 h-5 text-brand-mint" data-id="element-230" /> Secure
            Application
          </div>
          <div className="flex items-center gap-2" data-id="element-231">
            <Clock className="w-5 h-5 text-brand-mint" data-id="element-232" /> Instant Decisions
          </div>
          <div className="flex items-center gap-2" data-id="element-233">
            <CheckCircle2 className="w-5 h-5 text-brand-mint" data-id="element-234" /> High Approval
            Rates
          </div>
        </div>
      </div>
    </section>;
}
function Footer() {
  return <footer className="bg-brand-navy text-slate-300 py-16 border-t border-white/10" data-id="element-235">
      <div className="container mx-auto px-6 lg:px-12" data-id="element-236">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-20 mb-12" data-id="element-237">
          {/* Column 1: Brand */}
          <div data-id="element-238">
            <div className="flex items-center gap-3 text-white mb-6" data-id="element-239">
              <img src="https://cdn.magicpatterns.com/uploads/17ULAUNguMCwFMBzuxgSYH/pasted-image.png" alt="Colesville Dental Center Logo" className="h-10 w-auto object-contain  invert brightness-0" data-id="element-240" />
              <span className="text-2xl font-bold tracking-wide" data-id="element-241">
                COLESVILLE
              </span>
            </div>
            <p className="text-sm leading-relaxed mb-8 text-slate-300" data-id="element-242">
              Providing high-quality, pain-free, and personalized dental care.
              Your comfort and confidence in your smile are our top priorities.
            </p>
            <a href="https://colesvilledentalcenter.com/" className="inline-flex items-center gap-2 bg-gradient-to-r from-brand-teal to-brand-accent text-white px-6 py-3 rounded-full font-bold text-sm hover:shadow-lg hover:shadow-brand-teal/30 transition-all duration-300" data-id="element-243">
              <Calendar className="w-5 h-5" data-id="element-244" /> BOOK AN APPOINTMENT
            </a>
          </div>

          {/* Column 2: Get In Touch */}
          <div data-id="element-245">
            <h4 className="text-brand-teal font-bold text-lg mb-8" data-id="element-246">
              Get In Touch
            </h4>
            <ul className="space-y-6 text-sm" data-id="element-247">
              <li className="flex items-center gap-4" data-id="element-248">
                <div className="bg-white/10 border border-brand-teal/30 p-3 rounded-xl shrink-0" data-id="element-249">
                  <Phone className="w-5 h-5 text-brand-teal" data-id="element-250" />
                </div>
                <div data-id="element-251">
                  <div className="font-bold text-white mb-0.5" data-id="element-252">Phone</div>
                  <div className="text-slate-300" data-id="element-253">301-742-2231</div>
                </div>
              </li>
              <li className="flex items-center gap-4" data-id="element-254">
                <div className="bg-white/10 border border-brand-teal/30 p-3 rounded-xl shrink-0" data-id="element-255">
                  <Mail className="w-5 h-5 text-brand-teal" data-id="element-256" />
                </div>
                <div data-id="element-257">
                  <div className="font-bold text-white mb-0.5" data-id="element-258">Email</div>
                  <div className="text-slate-300" data-id="element-259">
                    dentallcentertc@gmail.com
                  </div>
                </div>
              </li>
              <li className="flex items-center gap-4" data-id="element-260">
                <div className="bg-white/10 border border-brand-teal/30 p-3 rounded-xl shrink-0" data-id="element-261">
                  <MapPin className="w-5 h-5 text-brand-teal" data-id="element-262" />
                </div>
                <div data-id="element-263">
                  <div className="font-bold text-white mb-0.5" data-id="element-264">Location</div>
                  <div className="text-slate-300 leading-relaxed" data-id="element-265">
                    8737 Colesville Road #301
                    <br data-id="element-266" />
                    Silver Spring, MD, 20910
                  </div>
                </div>
              </li>
            </ul>
          </div>

          {/* Column 3: Operating Hours */}
          <div data-id="element-267">
            <div className="flex items-center gap-2 mb-8" data-id="element-268">
              <Clock className="w-5 h-5 text-brand-teal" data-id="element-269" />
              <h4 className="text-brand-teal font-bold text-lg" data-id="element-270">
                Operating Hours
              </h4>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-id="element-271">
              <ul className="space-y-4 text-sm" data-id="element-272">
                <li className="flex justify-between items-center pb-4 border-b border-white/10" data-id="element-273">
                  <span className="text-slate-300" data-id="element-274">Monday</span>
                  <span className="font-bold text-white" data-id="element-275">
                    9:00 AM - 5:00 PM
                  </span>
                </li>
                <li className="flex justify-between items-center pb-4 border-b border-white/10" data-id="element-276">
                  <span className="text-slate-300" data-id="element-277">Tue, Thu &amp; Fri</span>
                  <span className="font-bold text-white" data-id="element-278">
                    2:00 PM - 7:00 PM
                  </span>
                </li>
                <li className="flex justify-between items-center" data-id="element-279">
                  <span className="text-slate-300" data-id="element-280">Wednesday</span>
                  <span className="font-bold text-white" data-id="element-281">
                    9:00 AM - 2:00 PM
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-xs" data-id="element-282">
          <p data-id="element-283">
            &copy; {new Date().getFullYear()} Colesville Dental Center. All
            rights reserved.
          </p>
          <div className="flex gap-6" data-id="element-284">
            <a href="#" className="hover:text-white transition-colors" data-id="element-285">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-286">
              Terms of Service
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-287">
              Accessibility
            </a>
          </div>
        </div>
      </div>
    </footer>;
}